// src/services/budgetQuoteService.js
/**
 * Budget Quote Service
 * Handles all API calls for budget quotes (presupuestos)
 * 
 * Backend API: /apitest/budget-quotes
 * Documentation: FrontEnd Documentation/DECEMBER TASKS/PRESUPUESTO-PROPER-IMPLEMENTATION-GUIDE.md
 */

import axios from 'axios';
import { generateExternalReference } from '../utils/invoiceUtils';
import { NODE_BACKEND_API_URL } from '../config';

const BUDGET_QUOTES_API_URL = `${NODE_BACKEND_API_URL}/budget-quotes`;

/**
 * Get auth headers with JWT token
 * @returns {Object} Headers with authorization
 */
const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

/**
 * Transform cart items to backend format
 * @param {Array} cartItems - Cart items from cart context
 * @returns {Array} Transformed items for backend
 */
const transformCartItems = (cartItems) => {
    return cartItems.map(item => {
        const splitter = item.splitter || 1;
        const quantity = item.quantity || 1;
        const retailPrice = item.RetailPrice || 0;
        return {
            ProductId: item.ProductId || null,
            ProductCode: item.ProductCode || null,
            EAN: item.EAN || null,
            ProductDescription: item.ProductDescription || '',
            BrandName: item.BrandName || null,
            CategoryName: item.CategoryName || null,
            quantity,
            splitter,
            unidad_medida: item.unidad_medida || 'UNI',
            RetailPrice: retailPrice,
            // Fix: divide by splitter so stored line total matches what the customer sees
            precio_total: (retailPrice / splitter) * quantity,
            alicuota_iva: item.alicuota_iva || 0,
            StockQuantity: item.StockQuantity || null,
            // Fix: preserve volume pricing flag so it is stored correctly in DB
            isVolumePricingActive: item.isVolumePricingActive || false
        };
    });
};

export const budgetQuoteService = {
    /**
     * Create a new budget quote
     * @param {Object} quoteData - Quote data from cart
     * @returns {Promise} Response with quote details
     */
    createBudgetQuote: async (quoteData) => {
        try {
            const payload = {
                cartItems: transformCartItems(quoteData.cartItems || []),
                subtotal: quoteData.subtotal || 0,
                descuentoPorcentaje: quoteData.discountPercentage || 0,
                descuentoImporte: quoteData.discountAmount || 0,
                total: quoteData.total || 0,
                clientData: quoteData.clientData || null,
                paymentMethod: quoteData.paymentMethod || null,
                paymentTerms: quoteData.paymentTerms || null,
                notas: quoteData.notas || null,
                externalReference: quoteData.externalReference || generateExternalReference(),
                cartSessionId: quoteData.sessionId || null,
                validityDays: quoteData.validityDays || 30
            };
            
            console.log('[BudgetQuote] Creating budget quote:', payload);
            
            const response = await axios.post(
                `${BUDGET_QUOTES_API_URL}/create`,
                payload,
                { headers: getAuthHeaders() }
            );
            
            console.log('[BudgetQuote] Quote created successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error creating quote:', error.response?.data || error.message);
            throw error;
        }
    },
    
    /**
     * Get all budget quotes with filters
     * @param {Object} filters - { status, dateFrom, dateTo, page, pageSize }
     * @returns {Promise} Response with quotes array and pagination
     */
    getBudgetQuotes: async (filters = {}) => {
        try {
            const params = {
                status: filters.status || undefined,
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                page: filters.page || 1,
                pageSize: filters.pageSize || 20
            };
            
            console.log('[BudgetQuote] Fetching quotes with filters:', params);
            
            const response = await axios.get(BUDGET_QUOTES_API_URL, {
                params,
                headers: getAuthHeaders()
            });
            
            console.log('[BudgetQuote] Quotes fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error fetching quotes:', error.response?.data || error.message);
            throw error;
        }
    },
    
    /**
     * Get budget quote detail with items
     * @param {number} quoteId - Quote ID
     * @returns {Promise} Response with quote and items
     */
    getBudgetQuoteDetail: async (quoteId) => {
        try {
            console.log('[BudgetQuote] Fetching quote detail for ID:', quoteId);
            
            const response = await axios.get(
                `${BUDGET_QUOTES_API_URL}/${quoteId}`,
                { headers: getAuthHeaders() }
            );
            
            console.log('[BudgetQuote] Quote detail fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error fetching quote detail:', error.response?.data || error.message);
            throw error;
        }
    },
    
    /**
     * Update budget quote status
     * @param {number} quoteId - Quote ID
     * @param {string} status - New status (PENDIENTE, ACEPTADO, RECHAZADO, VENCIDO, CONVERTIDO)
     * @param {Object} metadata - Additional data (convertedInvoiceId, rejectionReason)
     * @returns {Promise} Response
     */
    updateQuoteStatus: async (quoteId, status, metadata = {}) => {
        try {
            const payload = {
                status,
                convertedInvoiceId: metadata.convertedInvoiceId || null,
                rejectionReason: metadata.rejectionReason || null
            };
            
            console.log('[BudgetQuote] Updating quote status:', { quoteId, payload });
            
            const response = await axios.put(
                `${BUDGET_QUOTES_API_URL}/${quoteId}/status`,
                payload,
                { headers: getAuthHeaders() }
            );
            
            console.log('[BudgetQuote] Status updated successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error updating status:', error.response?.data || error.message);
            throw error;
        }
    },
    
    /**
     * Load quote items to cart
     * @param {number} quoteId - Quote ID
     * @returns {Promise} Response with cart items and metadata
     */
    loadQuoteToCart: async (quoteId) => {
        try {
            console.log('[BudgetQuote] Loading quote to cart:', quoteId);
            
            const response = await axios.post(
                `${BUDGET_QUOTES_API_URL}/${quoteId}/to-cart`,
                {},
                { headers: getAuthHeaders() }
            );
            
            console.log('[BudgetQuote] Quote loaded to cart:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error loading quote to cart:', error.response?.data || error.message);
            throw error;
        }
    },

    /**
     * Auto-expire old quotes (maintenance endpoint)
     * @returns {Promise} Response with count of expired quotes
     */
    expireOldQuotes: async () => {
        try {
            console.log('[BudgetQuote] Expiring old quotes...');
            
            const response = await axios.post(
                `${BUDGET_QUOTES_API_URL}/expire-old`,
                {},
                { headers: getAuthHeaders() }
            );
            
            console.log('[BudgetQuote] Quotes expired:', response.data);
            return response.data;
        } catch (error) {
            console.error('[BudgetQuote] Error expiring quotes:', error.response?.data || error.message);
            throw error;
        }
    }
};

export default budgetQuoteService;
